import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type LocationPermissionState = PermissionState | 'unsupported';

export interface CurrentLocation {
  latitude: number;
  longitude: number;
  accuracy: number;
}

export interface LocationAccessResult {
  granted: boolean;
  state: LocationPermissionState;
  location?: CurrentLocation;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class LocationService {
  private permissionStatus: PermissionStatus | null = null;
  private monitoringStarted = false;
  public myLatitude: number | null = null;
  public myLongitude: number | null = null;
  public accuracy: number | null = null;
  private readonly permissionStateSubject = new BehaviorSubject<LocationPermissionState>('prompt');
  readonly permissionState$ = this.permissionStateSubject.asObservable();

  constructor() { }

  getPermissionStateSnapshot(): LocationPermissionState {
    return this.permissionStateSubject.value;
  }

  async startPermissionMonitoring(): Promise<LocationPermissionState> {
    return this.refreshPermissionState(true);
  }

  async refreshPermissionState(attachListener = false): Promise<LocationPermissionState> {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      this.setPermissionState('unsupported');
      return 'unsupported';
    }

    if (!navigator.permissions?.query) {
      const fallbackState = this.permissionStateSubject.value === 'granted' ? 'granted' : 'prompt';
      this.setPermissionState(fallbackState);
      return fallbackState;
    }

    try {
      if (!this.permissionStatus) {
        this.permissionStatus = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
      }

      if (attachListener && !this.monitoringStarted) {
        this.permissionStatus.onchange = () => {
          this.setPermissionState(this.permissionStatus?.state || 'prompt');
        };
        this.monitoringStarted = true;
      }

      this.setPermissionState(this.permissionStatus.state);
      return this.permissionStatus.state;
    } catch {
      this.setPermissionState('prompt');
      return 'prompt';
    }
  }

  async ensureLocationAccess(): Promise<LocationAccessResult> {
    const initialState = await this.refreshPermissionState(true);

    if (initialState === 'unsupported') {
      return {
        granted: false,
        state: initialState,
        message: this.getPermissionHelpMessage(initialState)
      };
    }

    try {
      const location = await this.getCurrentLocation();
      this.setPermissionState('granted');

      return {
        granted: true,
        state: 'granted',
        location,
        message: this.getPermissionHelpMessage('granted')
      };
    } catch {
      const updatedState = await this.refreshPermissionState();

      return {
        granted: false,
        state: updatedState,
        message: this.getPermissionHelpMessage(updatedState)
      };
    }
  }

  getCurrentLocation(): Promise<CurrentLocation> {
    return new Promise((resolve, reject) => {

      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        this.setPermissionState('unsupported');
        reject(new Error('Geolocation not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        position => {
          this.setPermissionState('granted');
          this.myLatitude = position.coords.latitude;
          this.myLongitude = position.coords.longitude;
          this.accuracy = position.coords.accuracy;   
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        error => {
          if (error?.code === error.PERMISSION_DENIED) {
            this.setPermissionState('denied');
          }
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  }

  getPermissionHelpMessage(state: LocationPermissionState = this.permissionStateSubject.value): string {
    switch (state) {
      case 'granted':
        return 'Location access is enabled.';
      case 'denied':
        return 'Location access is blocked. Use the browser site settings near the address bar, change Location to Allow, then click "Check location again".';
      case 'unsupported':
        return 'This browser does not support location access. Please use a browser with geolocation enabled.';
      default:
        return 'Location access is required to use the application. Please choose Allow when your browser asks for permission.';
    }
  }

  private setPermissionState(state: LocationPermissionState) {
    this.permissionStateSubject.next(state);
  }
}
